import { Stack, Typography } from '@mui/material';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { GridPagination } from '@mui/x-data-grid';
import { toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { formatPrice } from '@/common/utils/money';
import { DataGrid } from '@/components/DataGrid';
import { ExportToCSVButton } from '../../components/ExportToCSVButton';
import {
  buildCsv,
  downloadCsv,
} from '../../ReportsExport/ReportsExport.helper';
import type { PlatformFeeRow } from '../hooks/useGetPlatformFeeData';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const columns: GridColDef<PlatformFeeRow>[] = [
  {
    field: 'fullName',
    headerName: 'Team Member',
    flex: 1,
    minWidth: 160,
  },
  {
    field: 'payorName',
    headerName: "Who's paying",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string | null) => value ?? '–',
  },
  {
    field: 'isExemptFromPlatformFee',
    headerName: 'Exempt',
    flex: 0.5,
    minWidth: 100,
    valueFormatter: (value: boolean) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'hasEnteredPaymentMethod',
    headerName: 'Payment method on file',
    flex: 1,
    minWidth: 180,
    valueFormatter: (_value: boolean | null, row: PlatformFeeRow) => {
      if (row.isExemptFromPlatformFee) return '–';
      return row.hasEnteredPaymentMethod ? 'Yes' : 'No';
    },
  },
  {
    field: 'pastDueBalance',
    headerName: 'Past Due Balance',
    flex: 1,
    minWidth: 150,
    // A $0 balance shows a dash (matching the other columns) rather than "$0.00".
    valueFormatter: (value: number) =>
      value > 0 ? formatPrice(value, true, true) : '–',
  },
];

interface Props {
  rows: PlatformFeeRow[];
  isLoading: boolean;
}

export function PlatformFeeTable({ rows, isLoading }: Props) {
  const isMobile = useIsMobile('md');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'fullName', sort: 'asc' },
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleExportCsv = useCallback(() => {
    try {
      const csv = buildCsv(rows, columns);
      const filename = `Platform-Fee-Status-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      downloadCsv(csv, filename);
      toast('success', 'Platform Fee Status Report exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    }
  }, [rows]);

  const rowCount = rows.length;

  const loadingRows = useMemo(
    () =>
      Array.from({ length: paginationModel.pageSize }, (_, i) => ({
        identityUserId: String(i),
        fullName: '',
        payorName: null,
        isExemptFromPlatformFee: false,
        hasEnteredPaymentMethod: null,
        pastDueBalance: 0,
      })),
    [paginationModel.pageSize],
  );

  return (
    <Stack sx={{ gap: 2 }} id="platform-fee-table">
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'center', md: 'flex-end' },
        }}
      >
        <Stack sx={{ gap: 1 }}>
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            Platform Fee
          </Typography>
        </Stack>
        <ExportToCSVButton
          onClick={handleExportCsv}
          disabled={rows.length === 0}
        />
      </Stack>
      <DataGrid
        variant="default"
        columns={columns}
        disableColumnFilter
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        rows={isLoading ? loadingRows : rows}
        getRowId={(row) => row.identityUserId}
        rowCount={rowCount}
        loading={isLoading}
        paginationMode="client"
        sortingMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        slots={{
          pagination: () =>
            rowCount > DEFAULT_PAGE_SIZE ? <GridPagination /> : null,
          noRowsOverlay: () => (
            <Stack
              direction="column"
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                No team members found
              </Typography>
            </Stack>
          ),
        }}
        sx={{
          ...(rowCount === 0 && {
            '--DataGrid-overlayHeight': '60px',
          }),
          '& .MuiTablePagination-root': {
            justifyContent: 'flex-start',
            display: 'flex',
          },
          '& .MuiToolbar-root': {
            paddingLeft: 0,
          },
        }}
      />
    </Stack>
  );
}
